import { z } from 'zod';
import { LocalTimeOfDay, WeekdayName } from './messaging.js';
/**
 * **How this business decides what to offer** — `docs/lovespa-gaps-plan.md`
 * A2, A3 and A4, and `docs/lovespa-discovery.md` §2.2, §2.4 and §2.5.
 *
 * Three knobs, one document, and all three are the *house's* answer rather than
 * the platform's. They live here beside `OrderTiming` and `GuestBooking`
 * because they are the same kind of thing — per-tenant policy read off
 * `tenant_config.config`, which the store **casts** rather than parses — and
 * every one of them therefore arrives through a tolerant reader at the bottom
 * of this file rather than through a `??` at each call site.
 *
 * The owner of Love Spa Frenchtown said all three of them out loud, and her
 * sentences are the specification:
 *
 *  - **A2, the gap.** *"I do a half an hour between clients"* — always, every
 *    service. Today a buffer is a property of an item, so "always" means typing
 *    30 onto twenty-one rows and remembering it on the twenty-second.
 *    `default_buffer_after_min` is the tenant's answer, and an item's own
 *    number — `0` included, since migration 0064 — still wins.
 *  - **A3, the lead.** *"I like being able to tell people I'm booked for two
 *    weeks."* She answers texts at the end of the business day and never on a
 *    Sunday, so a slot two hours from now is not a slot she can honour.
 *    `min_lead_minutes` and `same_day_cutoff` are that floor.
 *  - **A4, the shape.** *"I don't want to offer them the 11 o'clock, I want to
 *    offer the 3 o'clock… I'm like a jigsaw puzzle how I fit people in."* The
 *    operator put her rule back to her as *"after this one or before the other
 *    one, not somewhere in the middle, because then your day is destroyed"*.
 *    `offer_policy` is that rule, in three named modes — **and we do not yet
 *    know which one is hers**, which is why the deliverable includes
 *    `explainOffers` and the console's simulator rather than a guess.
 *
 * ## What is deliberately not here
 *
 * There is no per-item "explicitly zero" flag. `item_scheduling.buffer_*_min`
 * *was* `NOT NULL DEFAULT 0` until migration 0064, so a zero in that column
 * could not be told apart from a column nobody had touched — which meant **the
 * tenant default applied to every item sitting at 0**, and an item that wanted
 * "no gap at all although the house has one" needed a column and therefore a
 * migration. 0064 is that migration: the pair is nullable, `NULL` is the
 * house's and `0` is none, and every `0` the fleet carried was spelled `NULL`
 * because that is all it had ever meant. The reasoning that ruled out the
 * alternative is kept because it still holds: a sentinel like `-1`
 * would be a third meaning for a `CHECK (>= 0)` column, and a policy list of
 * "items exempt from the default" would be the same fact in two places.
 */
/* ------------------------------------------------------------------ */
/* A2 — the tenant-wide gap                                            */
/* ------------------------------------------------------------------ */
/** A buffer, in minutes. The same bounds `item_scheduling` has. */
export const BufferMinutes = z.number().int().min(0).max(240);
/** `min_lead_minutes`' bounds: nothing, to thirty days. */
export const MinLeadMinutes = z.number().int().min(0).max(43_200);
export const NO_BUFFER_DEFAULTS = { before_min: 0, after_min: 0 };
/* ------------------------------------------------------------------ */
/* A4 — the offer policy                                               */
/* ------------------------------------------------------------------ */
/**
 * **Which free starts the house is willing to name.**
 *
 *  - `open` — every free start the grid produces. What every tenant has had
 *    since the engine existed, and therefore the default.
 *  - `no_slivers` — drop a start whose leftover free time on either side (to
 *    the neighbouring occupied span, or to the edge of the open window) is
 *    greater than nothing and shorter than `min_useful_gap_min`. The owner:
 *    *"if I get an hour free, I kinda like it"* — a twenty-minute hole is not
 *    an hour, it is a hole.
 *  - `touching` — keep only a start whose occupied span **abuts** something: an
 *    existing occupied span, or the edge of the open window, within one grid
 *    step. On a day with nothing booked on that resource there is nothing to
 *    abut, so the anchors decide instead.
 *
 * The three are a ladder rather than three unrelated settings, and the engine
 * walks down it rather than ever answering "nothing" while `open` had
 * something: `touching → no_slivers → open`, per ask. A policy that cost a
 * booking would be a policy an owner switches off within a week.
 */
export const OfferMode = z.enum(['open', 'no_slivers', 'touching']);
/** The ladder, strictest first. The engine relaxes along it, left to right. */
export const OFFER_MODES_STRICTEST_FIRST = ['touching', 'no_slivers', 'open'];
/**
 * How the kept set is **ordered** — never which slots are in it.
 *
 *  - `chronological` — earliest first, which is what every surface has always
 *    drawn and therefore the default.
 *  - `packing` — the jigsaw order: a start that closes a hole on both sides
 *    first, then one that closes a hole on one side, then an anchor, then the
 *    rest; ties chronological. It is an *ordering*, so nothing becomes
 *    unbookable by being ranked last — which is the difference between a
 *    preference and a rule, and the reason this is a separate field from
 *    `mode`.
 */
export const OfferRank = z.enum(['chronological', 'packing']);
/**
 * A stretch of the local week that is **not offered to the public**.
 *
 * Her lunch, the school run, the hour she keeps for paperwork. It behaves like
 * a blackout for a guest or a partner agent and like nothing at all for a
 * member of staff, because the point is that she can still put somebody in it
 * when she decides to — a blackout row would take it away from her too.
 *
 * Days are **names**, Monday-first, the same `WeekdayName` vocabulary the
 * reminders config uses. An integer weekday in a hand-edited JSONB document is
 * a Sunday/Monday off-by-one that nobody finds for a week.
 */
export const ProtectedWindow = z.object({
    days: z.array(WeekdayName).min(1).max(7),
    /** Local `HH:MM`, inclusive. */
    start: LocalTimeOfDay,
    /** Local `HH:MM`, exclusive — half-open, like every other interval here. */
    end: LocalTimeOfDay,
});
/**
 * How many of the ranked set a surface should **lead with**.
 *
 * Advisory ordering data and nothing else: the engine returns the whole kept
 * set and flags the first N, because a phone agent reading six times out loud
 * is a phone agent nobody lets finish, while a guest scrolling a grid wants all
 * of them. A cap that actually removed slots would be the fourth mode, and the
 * modes are where that decision belongs.
 */
export const OfferTopN = z.object({
    partner: z.number().int().min(1).max(50).optional(),
    guest: z.number().int().min(1).max(50).optional(),
});
export const TenantOfferPolicy = z.object({
    mode: OfferMode.default('open'),
    /**
     * The shortest leftover gap that still counts as useful, in minutes.
     *
     * `.optional()` rather than a number, because the honest default is a fact
     * about the tenant's own catalog rather than a constant: **the shortest
     * occupied block any active scheduled item needs** — its duration plus its
     * resolved buffers. A gap that cannot hold the smallest thing she sells is a
     * gap she cannot sell. The engine computes it (`packages/scheduling`,
     * `minUsefulGapMin`) from the context it already has, so it costs no query
     * and it moves when her menu does.
     */
    min_useful_gap_min: z.number().int().min(5).max(480).optional(),
    /**
     * The starts offered on a resource-day with **nothing booked on it** when the
     * mode is `touching`, as local `HH:MM`.
     *
     * Absent means the engine's own default: the first and last candidate of each
     * open window — the window's opening, and the latest start that still fits.
     * Which is the empty-day reading of "abuts an edge", and is why an empty
     * Tuesday under `touching` offers two times rather than none.
     */
    anchors: z.array(LocalTimeOfDay).max(12).optional(),
    /** Never offered to a guest or a partner. Staff may still book into it. */
    protected: z.array(ProtectedWindow).max(14).optional(),
    rank: OfferRank.default('chronological'),
    top_n: OfferTopN.optional(),
});
/** The policy a tenant that has configured nothing behaves as. */
export const OPEN_OFFER_POLICY = { mode: 'open', rank: 'chronological' };
export const NO_LEAD_TIME = { min_lead_minutes: 0, same_day_cutoff: null };
/* ------------------------------------------------------------------ */
/* The patch — one shape, every door                                   */
/* ------------------------------------------------------------------ */
/**
 * The five keys this policy occupies in `tenant_config.config`, in the order
 * every surface prints them.
 *
 * Exported because three places need the same list and none of them should own
 * it: `apps/admin-api`'s route (which refuses a key outside it), `apps/cli`'s
 * `scheduling policy` verb (which maps flags onto it), and `packages/store`'s
 * `setSchedulingPolicy` (which turns it into one atomic statement). A fourth
 * copy would be a key one door writes and another cannot see.
 */
export const SCHEDULING_POLICY_KEYS = [
    'default_buffer_before_min',
    'default_buffer_after_min',
    'min_lead_minutes',
    'same_day_cutoff',
    'offer_policy',
];
/**
 * **What a caller may change about the house's scheduling rules**, and nothing
 * else.
 *
 * `.strict()` rather than tolerant, deliberately and unlike the *readers* below:
 * a reader is salvaging a document that may have been hand-edited with `psql`,
 * while this is a form or a command line, and a misspelled key arriving here
 * silently would be a policy an operator believes they set.
 *
 * Every key is optional and every key is nullable, and the two mean different
 * things. **Absent** leaves whatever is stored alone; **`null` removes the key**
 * — because "this tenant has no offer policy at all" is not the same fact as
 * "this tenant's offer policy is `open`", and `offerPolicyOf` tells them apart.
 *
 * It lives in `domain` rather than in either surface because there must be
 * exactly one validator. A value the console accepted and the CLI refused — or
 * worse, one either accepted and the engine ignored — is the drift this package
 * exists to prevent.
 */
export const SchedulingPolicyPatch = z
    .object({
    default_buffer_before_min: BufferMinutes.nullable().optional(),
    default_buffer_after_min: BufferMinutes.nullable().optional(),
    min_lead_minutes: MinLeadMinutes.nullable().optional(),
    same_day_cutoff: LocalTimeOfDay.nullable().optional(),
    offer_policy: TenantOfferPolicy.nullable().optional(),
})
    .strict();
/* ------------------------------------------------------------------ */
/* The tolerant readers                                                */
/* ------------------------------------------------------------------ */
function document(config) {
    return config ?? null;
}
/** A number this document can be trusted for, or `fallback`. */
function intOr(raw, schema, fallback) {
    return schema.safeParse(raw).success ? raw : fallback;
}
/**
 * **The tenant's default buffers**, from a document that almost certainly
 * predates the field.
 *
 * `defaultLocaleOf`'s shape and `defaultLocaleOf`'s reason. `0` is the answer
 * for absent, for unreadable and for hand-mangled, and `0` is exactly today's
 * behaviour — which is what makes this rollout a no-op for every tenant in the
 * fleet rather than a quiet 30 minutes added to twenty-one catalogs.
 */
export function bufferDefaultsOf(config) {
    const doc = document(config);
    if (doc === null)
        return NO_BUFFER_DEFAULTS;
    return {
        before_min: intOr(doc['default_buffer_before_min'], BufferMinutes, 0),
        after_min: intOr(doc['default_buffer_after_min'], BufferMinutes, 0),
    };
}
/**
 * **The minimum lead time and the same-day cut-off**, likewise.
 *
 * Zero and `null` mean "no floor beyond the one every surface has always had":
 * never a time that has already gone.
 */
export function leadTimeOf(config) {
    const doc = document(config);
    if (doc === null)
        return NO_LEAD_TIME;
    const cutoff = doc['same_day_cutoff'];
    return {
        min_lead_minutes: intOr(doc['min_lead_minutes'], MinLeadMinutes, 0),
        same_day_cutoff: LocalTimeOfDay.safeParse(cutoff).success ? cutoff : null,
    };
}
/**
 * **This tenant's offer policy, or `null` when it has none.**
 *
 * `null` — not `OPEN_OFFER_POLICY` — so a caller can tell "never configured"
 * from "configured, and the answer is open", which are the same behaviour and
 * different facts; the store writes the field onto the scheduling context only
 * in the first case, and that is what makes the absent-config answers
 * byte-identical to the engine before this wave.
 *
 * Salvaged field by field, `staffBrandingOf`'s shape: one hand-edited
 * `protected` window with a malformed time costs that list and not the mode.
 */
export function offerPolicyOf(config) {
    const raw = document(config)?.['offer_policy'];
    if (raw === null || typeof raw !== 'object' || Array.isArray(raw))
        return null;
    const doc = raw;
    const out = {};
    for (const [key, field] of Object.entries(TenantOfferPolicy.shape)) {
        const value = doc[key];
        if (value === undefined)
            continue;
        if (field.safeParse(value).success)
            out[key] = value;
    }
    const parsed = TenantOfferPolicy.safeParse(out);
    return parsed.success ? parsed.data : null;
}
//# sourceMappingURL=scheduling-policy.js.map